<template>
  <div class="min-h-full bg-transparent dark:bg-[#0B1120] text-gray-900 dark:text-[#F1F5F9] flex items-center justify-center p-4">
    <div class="max-w-md w-full text-center">
      <!-- Animated 404 Number -->
      <div class="relative mb-8">
        <h1
          class="text-8xl md:text-9xl font-bold text-indigo-600 opacity-20 select-none animate-pulse"
        >
          404
        </h1>

        <!-- Floating School Elements -->
        <div class="absolute inset-0 flex items-center justify-center">
          <div class="relative">
            <!-- Animated Book -->
            <div
              class="absolute -top-12 -left-8 transform transition-transform duration-1000 ease-in-out"
              :class="{ 'rotate-12 translate-y-2': isAnimating }"
            >
              <div
                class="w-8 h-10 bg-red-500 rounded-sm shadow-lg animate-bounce"
                style="animation-delay: 0.2s"
              >
                <div class="w-full h-1 bg-red-600 mt-2 rounded"></div>
                <div class="w-full h-1 bg-red-600 mt-1 rounded"></div>
              </div>
            </div>

            <!-- Animated Pencil -->
            <div
              class="absolute -top-8 -right-6 transform transition-transform duration-1000 ease-in-out"
              :class="{ '-rotate-45 translate-x-2': isAnimating }"
            >
              <div
                class="w-2 h-12 bg-yellow-400 rounded-full shadow-lg animate-bounce"
                style="animation-delay: 0.4s"
              >
                <div class="w-2 h-3 bg-pink-300 rounded-full"></div>
                <div class="w-2 h-2 bg-gray-800 rounded-full mt-7"></div>
              </div>
            </div>

            <!-- Animated Apple -->
            <div
              class="absolute -bottom-6 left-4 transform transition-transform duration-1000 ease-in-out"
              :class="{ 'rotate-6 -translate-y-1': isAnimating }"
            >
              <div
                class="w-6 h-6 bg-red-400 rounded-full shadow-lg animate-bounce"
                style="animation-delay: 0.6s"
              >
                <div class="w-2 h-2 bg-green-500 rounded-full -mt-1 ml-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="space-y-6 animate-fade-in-up">
        <!-- Title -->
        <div class="space-y-2">
          <h2
            class="text-2xl md:text-3xl font-bold text-gray-800 animate-slide-in-left"
          >
            Oops! Page Not Found
          </h2>
          <p
            class="text-gray-600 text-sm md:text-base animate-slide-in-right"
            style="animation-delay: 0.2s"
          >
            Looks like this page took an unexpected field trip!
          </p>
        </div>

        <!-- Animated Illustration -->
        <div class="flex justify-center my-8">
          <div class="relative">
            <!-- School Building -->
            <div
              class="w-32 h-24 bg-blue-500 rounded-t-lg shadow-lg transform transition-transform duration-500 hover:scale-105"
            >
              <!-- Windows -->
              <div class="grid grid-cols-3 gap-2 p-2">
                <div
                  v-for="i in 6"
                  :key="i"
                  class="w-4 h-4 bg-yellow-200 rounded animate-twinkle"
                  :style="{ animationDelay: `${i * 0.3}s` }"
                ></div>
              </div>
              <!-- Door -->
              <div class="w-6 h-8 bg-red-600 rounded-t-lg mx-auto -mt-1"></div>
            </div>

            <!-- Flag -->
            <div class="absolute -top-2 left-1/2 transform -translate-x-1/2">
              <div class="w-1 h-8 bg-gray-600"></div>
              <div
                class="w-6 h-4 bg-red-500 -mt-6 ml-1 animate-wave origin-left"
                style="
                  clip-path: polygon(0 0, 100% 0, 85% 50%, 100% 100%, 0 100%);
                "
              ></div>
            </div>
          </div>
        </div>

        <!-- Message -->
        <div
          class="bg-white dark:bg-[#111C30] rounded-lg p-6 shadow-lg border border-transparent dark:border-[#263449] animate-fade-in"
          style="animation-delay: 0.4s"
        >
          <p class="text-gray-700 dark:text-[#A6B4C8] text-sm md:text-base leading-relaxed">
            Don't worry! Even the best users sometimes get lost in the hallways.
            Let's get you back to the main campus.
          </p>
        </div>

        <!-- Action Button -->
        <button
          @click="goHome"
          class="group relative inline-flex items-center justify-center px-8 py-3 text-lg font-semibold text-white bg-indigo-600 rounded-full shadow-lg hover:bg-indigo-700 transform transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 animate-bounce-in"
          style="animation-delay: 0.6s"
        >
          <svg
            class="w-5 h-5 mr-2 transform transition-transform duration-300 group-hover:-translate-x-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            ></path>
          </svg>
          Back to School

          <!-- Button shine effect -->
          <div
            class="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"
          ></div>
        </button>

        <!-- Fun Fact -->
        <div
          class="text-xs text-gray-500 animate-fade-in"
          style="animation-delay: 0.8s"
        >
          <p>
            💡 Fun fact: The first computer bug was actually a real bug found in
            1947!
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted } from "vue";
  import { useRouter } from "vue-router";

  const router = useRouter();
  const isAnimating = ref(false);

  onMounted(() => {
    // Start animations after component mounts
    setTimeout(() => {
      isAnimating.value = true;
    }, 500);

    // Cycle animations
    setInterval(() => {
      isAnimating.value = !isAnimating.value;
    }, 3000);
  });

  const goHome = () => {
    router.push("/");
  };
</script>

<style scoped>
  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slide-in-left {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slide-in-right {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes bounce-in {
    0% {
      opacity: 0;
      transform: scale(0.3);
    }
    50% {
      transform: scale(1.05);
    }
    70% {
      transform: scale(0.9);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes twinkle {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }

  @keyframes wave {
    0%,
    100% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(5deg);
    }
    75% {
      transform: rotate(-5deg);
    }
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.8s ease-out forwards;
  }

  .animate-slide-in-left {
    animation: slide-in-left 0.6s ease-out forwards;
  }

  .animate-slide-in-right {
    animation: slide-in-right 0.6s ease-out forwards;
  }

  .animate-fade-in {
    animation: fade-in 0.6s ease-out forwards;
  }

  .animate-bounce-in {
    animation: bounce-in 0.8s ease-out forwards;
  }

  .animate-twinkle {
    animation: twinkle 2s ease-in-out infinite;
  }

  .animate-wave {
    animation: wave 2s ease-in-out infinite;
  }

  /* Mobile optimizations */
  @media (max-width: 640px) {
    .animate-bounce {
      animation-duration: 2s;
    }
  }
</style>
